import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, Optional, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CropperImageOrientation } from '../../interfaces/image-properties.interface';
import { CropperAspectRatio, CropperDialogData, CropperDialogResult } from '../../interfaces/cropper-dialog.interface';
import { AspectRatio, aspectRatios as UtilsAspectRatios } from '../../utils/aspect-ratios';
import { CropperDialogComponent } from '../cropper-dialog/cropper-dialog.component';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';

export type imageTypes = 'avatar' | 'picture';

@Component({
  selector: 'cropper-image-control',
  templateUrl: './image-control.component.html',
  styleUrls: ['./image-control.component.scss']
})
export class ImageControlComponent implements OnInit {
  constructor(
    @Optional() protected dialog: MatDialog,
    @Optional() protected sanitizer: DomSanitizer
  ) {
    if (!this.dialog) {
      console.warn('MatDialog is not provided. Make sure to import MatDialogModule in your module.');
    }
    if (!this.sanitizer) {
      console.warn('DomSanitizer is not provided. Make sure to import BrowserModule or BrowserAnimationsModule in your root module.');
    }
  }

  @Input() imageSource: string | SafeUrl = '';
  @Input() disabledControl: boolean = false;
  @Input() type: imageTypes = 'picture';
  @Input() aspectRatio: number = 1;
  @Input() width: number = 300;
  @Input() height: number = 300;
  @Input() label: string = '';
  @Input() preview: boolean = true;
  @Input() prevImgCss: string = 'w-100';
  @Input() imageWidth: number = 320;
  @Input() imageHeight: number = 170;
  @Input() disableOrientationButton: boolean = false;
  @Input() disableAspectRatioButton: boolean = false;

  @Output() imageReady = new EventEmitter<Blob>();
  @Output() imageSelected = new EventEmitter<File>();
  @Output() imageCropped = new EventEmitter<Blob>();

  @ViewChild('inputField') inputField!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    // Initialize component
  }

  /**
   * @default true Horizontal
   * @example true: Horizontal | false: Vertical
   */
  orientation: boolean = true;
  @Input() set defaultOrientation(val: CropperImageOrientation) {
    this.orientation = (val === 'Horizontal');
  }

  aspectRatios: CropperAspectRatio[] = [...UtilsAspectRatios];
  @Input() set ratios(ratios: AspectRatio[]) {
    this.aspectRatios = [...UtilsAspectRatios].filter(r => ratios.includes(r.label));
  }

  croppedImage: CropperDialogResult | null = null;

  protected buildDataCropper(file: any) {
    return { 
      image: file, 
      width: this.imageWidth, 
      height: this.imageHeight, 
      ratios: this.aspectRatios,
      disableOrientationButton: this.disableOrientationButton,
      disableAspectRatioButton: this.disableAspectRatioButton,
      defaultOrientation: this.orientation,
    }
  }

  public fileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const dialogRef = this.openDialogRef(file);

      this.toDoAfterClosedDialog(dialogRef);
    }
  }

  protected openDialogRef(file: any):  MatDialogRef<any, CropperDialogResult> {
    return this.dialog.open<CropperDialogComponent, CropperDialogData, CropperDialogResult>(
      CropperDialogComponent, {data: this.buildDataCropper(file), disableClose: true, maxWidth: '800px'}
    );
  }

  protected toDoAfterClosedDialog(dialogRef: MatDialogRef<CropperDialogComponent, CropperDialogResult>) {
    dialogRef.afterClosed()
      .pipe(filter((result): result is CropperDialogResult => !!result))
      .subscribe(result => {
        this.croppedImage = result;
        this.imageSource = this.sanitizer.bypassSecurityTrustUrl(result.imageUrl);
        this.imageReady.emit(this.croppedImage?.blob);
        if (this.croppedImage?.blob) {
          this.imageCropped.emit(this.croppedImage.blob);
        }
      });
  }
}
